'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

export default function GoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

    if (!GA_MEASUREMENT_ID && !GTM_ID) return;

    // Track page views
    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : ''),
      });
    }

    // Track Web Vitals
    if ('web-vital' in window.performance) {
      // @ts-ignore
      import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB, onINP }) => {
        onCLS((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'CLS',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });

        onFID((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'FID',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });

        onLCP((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'LCP',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });

        onFCP((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'FCP',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });

        onTTFB((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'TTFB',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });

        onINP((metric) => {
          if (window.gtag && GA_MEASUREMENT_ID) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Web Vitals',
              event_label: metric.id,
              value: Math.round(metric.value),
              metric_name: 'INP',
              metric_value: metric.value,
              metric_delta: metric.delta,
              non_interaction: true,
            });
          }
        });
      });
    }
  }, [pathname, searchParams]);

  const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

  if (!GA_MEASUREMENT_ID && !GTM_ID) return null;

  return (
    <>
      {/* Google Analytics */}
      {GA_MEASUREMENT_ID && (
        <>
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          />
          <script
            id="google-analytics"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_MEASUREMENT_ID}', {
                  page_path: window.location.pathname,
                  send_page_view: true
                });
              `,
            }}
          />
        </>
      )}

      {/* Google Tag Manager */}
      {GTM_ID && (
        <>
          <script
            id="google-tag-manager"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${GTM_ID}');
              `,
            }}
          />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}
    </>
  );
}